import React, { useMemo } from 'react';
import { CheckCircle, AlertCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

const THRESHOLDS = { good: 85, fair: 60 };

function scoreLength(text) {
  const len = text.trim().length;
  if (len >= 800) return { score: 25, label: 'Good length', detail: `${len} characters — thorough and detailed` };
  if (len >= 400) return { score: 20, label: 'Decent length', detail: `${len} chars — consider adding more detail` };
  if (len >= 150) return { score: 12, label: 'Too short', detail: `${len} chars — aim for 400+ characters` };
  return { score: 5, label: 'Very short', detail: `${len} chars — clients prefer detailed proposals` };
}

function scoreSkillMentions(text, skills) {
  if (!skills || skills.length === 0) return { score: 25, label: 'No skills to check', detail: 'Job has no listed skills' };
  const lower = text.toLowerCase();
  const mentioned = skills.filter(s => {
    const name = (typeof s === 'string' ? s : s?.name || '').toLowerCase();
    return name && lower.includes(name);
  });
  const ratio = mentioned.length / skills.length;
  const score = Math.round(ratio * 25);
  if (ratio >= 0.6) return { score, label: 'Good skill coverage', detail: `${mentioned.length}/${skills.length} job skills mentioned` };
  if (ratio >= 0.3) return { score, label: 'Some skills mentioned', detail: `${mentioned.length}/${skills.length} — reference more job requirements` };
  return { score, label: 'Few skills mentioned', detail: `${mentioned.length}/${skills.length} — mirror the job posting's language` };
}

function scoreExamples(text) {
  const lower = text.toLowerCase();
  const patterns = [
    /for example/i, /such as/i, /i built/i, /i developed/i, /i created/i,
    /i designed/i, /i implemented/i, /my project/i, /portfolio/i, /github/i,
    /case study/i, /client\s/i, /result(s|ed)?\s/i, /increased/i, /reduced/i,
    /improved/i, /delivered/i, /achieved/i, /https?:\/\//i, /\d+%/i
  ];
  const matches = patterns.filter(p => p.test(text)).length;
  if (matches >= 4) return { score: 25, label: 'Strong examples', detail: 'Includes concrete experience and results' };
  if (matches >= 2) return { score: 18, label: 'Some examples', detail: 'Add more specific results or past work' };
  if (matches >= 1) return { score: 10, label: 'Weak examples', detail: 'Include past projects, metrics, or links' };
  return { score: 3, label: 'No examples found', detail: 'Mention specific past work, results, or links' };
}

function scoreClarity(text) {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const words = text.trim().split(/\s+/).length;
  let score = 25;
  const issues = [];

  // Check for very long sentences
  const longSentences = sentences.filter(s => s.trim().split(/\s+/).length > 40);
  if (longSentences.length > 0) { score -= 5; issues.push('some sentences are very long'); }

  // Check for repeated words (lazy writing)
  const wordArr = text.toLowerCase().match(/\b[a-z]{4,}\b/g) || [];
  const freq = {};
  wordArr.forEach(w => { freq[w] = (freq[w] || 0) + 1; });
  const overused = Object.entries(freq).filter(([, c]) => c > 5 && c / wordArr.length > 0.03);
  if (overused.length > 0) { score -= 3; issues.push(`"${overused[0][0]}" used ${overused[0][1]} times`); }

  // Check for all-caps shouting
  if (/[A-Z]{5,}/.test(text)) { score -= 3; issues.push('avoid ALL CAPS'); }

  // Check paragraph structure
  if (words > 100 && !text.includes('\n')) { score -= 4; issues.push('add paragraph breaks for readability'); }

  score = Math.max(score, 3);
  if (issues.length === 0) return { score, label: 'Clear writing', detail: 'Well-structured and readable' };
  return { score, label: 'Could improve clarity', detail: issues.join('; ') };
}

function getIcon(score, max) {
  const ratio = score / max;
  if (ratio >= 0.75) return <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />;
  if (ratio >= 0.45) return <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
  return <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />;
}

function getOverallColor(total) {
  if (total >= THRESHOLDS.good) return 'text-green-600';
  if (total >= THRESHOLDS.fair) return 'text-amber-600';
  return 'text-red-500';
}

function getBarColor(total) {
  if (total >= THRESHOLDS.good) return 'bg-green-500';
  if (total >= THRESHOLDS.fair) return 'bg-amber-500';
  return 'bg-red-400';
}

export function ProposalQualityScore({ coverLetter, job, collapsed, onToggle }) {
  const text = coverLetter || '';
  const skills = job?.skills || [];

  const checks = useMemo(() => {
    if (!text.trim()) return null;
    const length = scoreLength(text);
    const skillMentions = scoreSkillMentions(text, skills);
    const examples = scoreExamples(text);
    const clarity = scoreClarity(text);
    const total = length.score + skillMentions.score + examples.score + clarity.score;
    return { length, skillMentions, examples, clarity, total };
  }, [text, skills]);

  if (!text.trim()) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <p className="text-sm text-gray-400 text-center">Write a cover letter to see quality feedback</p>
      </div>
    );
  }

  const { length, skillMentions, examples, clarity, total } = checks;
  const items = [
    { key: 'length', label: 'Length', ...length },
    { key: 'skills', label: 'Skill mentions', ...skillMentions },
    { key: 'examples', label: 'Examples & proof', ...examples },
    { key: 'clarity', label: 'Clarity & structure', ...clarity },
  ];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`text-lg font-bold ${getOverallColor(total)}`}>{total}</span>
          <span className="text-sm text-gray-600">/ 100 Quality Score</span>
        </div>
        {collapsed
          ? <ChevronDown className="h-4 w-4 text-gray-400" />
          : <ChevronUp className="h-4 w-4 text-gray-400" />
        }
      </button>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100">
        <div
          className={`h-full transition-all duration-500 ${getBarColor(total)}`}
          style={{ width: `${total}%` }}
        />
      </div>

      {/* Detail items */}
      {!collapsed && (
        <div className="divide-y divide-gray-100">
          {items.map(item => (
            <div key={item.key} className="px-4 py-3 flex items-start gap-3">
              {getIcon(item.score, 25)}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{item.label}</span>
                  <span className="text-xs text-gray-400">{item.score}/25</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>
              </div>
            </div>
          ))}
          {total >= THRESHOLDS.good && (
            <div className="px-4 py-3 bg-green-50">
              <p className="text-xs text-green-700 font-medium">✨ Great proposal! Ready to submit.</p>
            </div>
          )}
          {total < THRESHOLDS.fair && (
            <div className="px-4 py-3 bg-red-50">
              <p className="text-xs text-red-600 font-medium">Consider improving your proposal before submitting.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
